import { connect } from 'react-redux'
import { compose, withProps, withHandlers, lifecycle } from 'recompose'
import isEqual from 'lodash/isEqual'
import { getCurrentStoreNumber, getCurrentStoreIsE2EAppointment } from 'selectors/getCurrentStore'
import { getQueryParams } from 'selectors/getPageLocation'
import withStepperState from 'components/common/hoc/withStepperState'
import { googleAnalytic3StepperActionBarClick } from 'actions/googleAnalytic'
import { setAppointmentConfirmationContactDetails } from 'components/AppointmentConfirmationPage/Checkout/components/AppointmentInfoDrawer/actions'
import { getCompletedSteps, getAppointmentFormValues } from './selectors'
import { getAppointmentDatesAndSlots } from '../Stepper/actions'
import { storeAppointmentContactDetails } from 'components/AppointmentConfirmationPage/Checkout/helpers'
import StepperContainer from '../Stepper/StepperContainer'
import { storeGoogleConversionTracker } from './helpers'
import {
  SCHEDULE_APPOINTMENT_THREE_STEPS_TITLE,
  REQUEST_APPOINTMENT_THREE_STEPS_TITLE
} from 'constant'
import { getRemovedPromotions, getActivePromotionsList } from 'components/QuickAppointmentPage/selectors'
import { setCurrentStep, setDeletedPromotions, setPreviousStep } from '../actions'
import { getUserData } from 'components/Appointment/selectors'

const mapStateToProps = state => {
  const {
    firstName,
    lastName,
    phoneNumber,
    email,
    catalogRefIds: selectedServices,
    activePromotions
  } = getAppointmentFormValues(state) || {}
  const { isPilotStore } = getUserData(state)

  return {
    steps: getCompletedSteps(state),
    appointmentResponse: state.appointment.appointmentResponse,
    contactDetails: { firstName, lastName, phoneNumber, email },
    selectedServices,
    currentStoreNum: getCurrentStoreNumber(state),
    URLQueryParams: getQueryParams(state),
    isE2EAppointment: getCurrentStoreIsE2EAppointment(state),
    isPilotStore,
    removedPromotions: getRemovedPromotions(state),
    activePromotionsFromState: getActivePromotionsList(state),
    activePromotionsFromForm: activePromotions
  }
}

const withNavigationHandlers = withHandlers({
  prevPageClick: ({
    hasPrevPage,
    currentStepIndex,
    setCurrentStepIndex,
    googleAnalytic3StepperActionBarClick
  }) => () => {
    if (hasPrevPage) {
      googleAnalytic3StepperActionBarClick({ threeStepper: currentStepIndex + 1 })
      setCurrentStepIndex(currentStepIndex - 1)
    }
  },
  nextPageClick: ({
    isDone,
    hasNextPage,
    currentStepIndex,
    setCurrentStepIndex,
    googleAnalytic3StepperActionBarClick,
    removedPromotions,
    activePromotionsFromState,
    activePromotionsFromForm,
    setDeletedPromotions
  }) => () => {
    if (isDone) {
      if (hasNextPage) {
        googleAnalytic3StepperActionBarClick({ threeStepper: currentStepIndex + 1 })
        setCurrentStepIndex(currentStepIndex + 1)

        // Checking if the current step is 2nd and next will be 3rd.
        // Also checking is promo was removed on 2nd step, so we
        // can update removedPromotions state for future manipulations.
        if (currentStepIndex === 1
          && !activePromotionsFromForm.length
          && !removedPromotions.length
          && activePromotionsFromState.length) {
          setDeletedPromotions(activePromotionsFromState)
        }

        // Checking if the current step is 1st and next will be 2nd.
        // Also checking if the promo was removed on 1st step, so we
        // can update removedPromotions state to empty array.
        if (currentStepIndex === 0 && removedPromotions.length) {
          setDeletedPromotions([])
        }
      }
    }
  },
  beforeNavigatingToDateTimeStep: ({
    setAppointmentDetailsResponseVO,
    getAppointmentDatesAndSlots,
    setIsLoading
  }) => ({ setSubmitAttemptFailed, submitAttemptFailed } = {}) => {
    setIsLoading(true)
    getAppointmentDatesAndSlots({ setSubmitAttemptFailed, submitAttemptFailed })
      .then(({ appointmentDetailsResponseVO }) => {
        setAppointmentDetailsResponseVO(appointmentDetailsResponseVO)
        setIsLoading(false)
      })
  }
})

const withStepProps = withProps(({ cartridges, steps, currentStepIndex, isE2EAppointment }) => {
  const { isDone, cartridgeType, component } = steps[currentStepIndex]
  const hasPrevPage = currentStepIndex > 0
  const hasNextPage = currentStepIndex < steps.length - 1
  let content = []

  if (typeof cartridgeType === 'object') {
    cartridgeType.forEach(cartType => content.push(cartridges.find(({ props: { type } = {}} = {}) =>
      type === cartType)))
  } else {
    content = cartridges.find(cartridge => cartridge.props.type === cartridgeType)
  }

  return {
    hasPrevPage,
    hasNextPage,
    isDone,
    content,
    Component: component,
    title: isE2EAppointment ? SCHEDULE_APPOINTMENT_THREE_STEPS_TITLE : REQUEST_APPOINTMENT_THREE_STEPS_TITLE,
    isE2EAppointment
  }
})

const withDateTimeStepIndex = withProps(() => ({ dateTimeStepIndex: 1 }))

const withLifecycles = lifecycle({
  componentDidMount() {
    const {
      URLQueryParams: {
        rwg_token: rwgToken
      } = {}
    } = this.props

    Boolean(rwgToken) && storeGoogleConversionTracker(rwgToken)
  },
  componentDidUpdate({ currentStepIndex: prevCurrentStepIndex }) {
    const {
      currentStepIndex,
      setCurrentStep,
      setPreviousStep
    } = this.props


    if (!isEqual(currentStepIndex, prevCurrentStepIndex)) {
      setCurrentStep(currentStepIndex + 1)
      setPreviousStep(prevCurrentStepIndex + 1)
    }
  },
  componentWillUnmount() {
    const { contactDetails, setAppointmentConfirmationContactDetails, setDeletedPromotions } = this.props
    setDeletedPromotions([])
    storeAppointmentContactDetails({ contactDetails, setAppointmentConfirmationContactDetails })
  }
})

export default compose(
  connect(mapStateToProps, {
    googleAnalytic3StepperActionBarClick,
    setAppointmentConfirmationContactDetails,
    getAppointmentDatesAndSlots,
    setCurrentStep,
    setPreviousStep,
    setDeletedPromotions
  }),
  withStepperState,
  withDateTimeStepIndex,
  withStepProps,
  withNavigationHandlers,
  withLifecycles
)(StepperContainer)
